import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Key, X } from 'lucide-react';
import { PermissionsService, type CreatePermissionData } from '../../services/security/permissions.service';
import { NotificationToast } from '../ui/NotificationToast';

const createPermissionSchema = z.object({
  nombre: z.string().min(1, 'Nombre es requerido').min(3, 'Mínimo 3 caracteres'),
  descripcion: z.string().optional()
});

type CreatePermissionFormData = z.infer<typeof createPermissionSchema>;

interface CreatePermissionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreatePermissionDrawer: React.FC<CreatePermissionDrawerProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Estados para notificaciones
  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors
  } = useForm<CreatePermissionFormData>({
    resolver: zodResolver(createPermissionSchema),
    defaultValues: {
      nombre: '',
      descripcion: ''
    }
  });

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      reset();
      clearErrors();
    } else {
      setIsVisible(false);
    }
  }, [isOpen, reset, clearErrors]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const onSubmit = async (data: CreatePermissionFormData) => {
    setIsLoading(true);
    
    try {
      const dataToSend: CreatePermissionData = {
        nombre: data.nombre.trim(),
        ...(data.descripcion?.trim() && { descripcion: data.descripcion.trim() })
      };
      
      await PermissionsService.createPermission(dataToSend);
      
      setNotificationConfig({
        title: '¡Éxito!',
        message: 'Permiso creado exitosamente',
        type: 'success'
      });
      setShowNotification(true);
      
      // Cerrar drawer después de un breve delay
      setTimeout(() => {
        handleClose();
        onSuccess();
      }, 1500);
      
    } catch (error: any) {
      console.error('Error creating permission:', error);
      setNotificationConfig({
        title: 'Error al crear permiso',
        message: error?.response?.data?.message || 'No se pudo crear el permiso',
        type: 'error'
      });
      setShowNotification(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-20 overflow-hidden" style={{ top: '90px' }}>
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={handleClose}
          aria-label="Cerrar drawer"
        />
        
        <section className={`absolute right-0 top-0 h-full w-full max-w-2xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div 
            className="h-full flex flex-col bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 bg-gray-100 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Key className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Crear Nuevo Permiso
                    </h2>
                    <p className="text-sm text-gray-600">
                      Agrega un nuevo permiso al sistema
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                  disabled={isLoading}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
                {/* Nombre */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre del Permiso *
                  </label>
                  <input
                    type="text"
                    {...register('nombre')}
                    className={`block w-full rounded-lg border px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${
                      errors.nombre 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    placeholder="Ej: crear, editar, eliminar, ver"
                    disabled={isLoading}
                  />
                  {errors.nombre && (
                    <p className="mt-1 text-sm text-red-600">{errors.nombre.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Usa nombres descriptivos como "crear", "editar", "eliminar", etc.
                  </p>
                </div>

                {/* Descripción */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción
                  </label>
                  <textarea
                    {...register('descripcion')}
                    rows={3}
                    className={`block w-full rounded-lg border px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none ${
                      errors.descripcion 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    placeholder="Describe qué permite hacer este permiso..."
                    disabled={isLoading}
                  />
                  {errors.descripcion && (
                    <p className="mt-1 text-sm text-red-600">{errors.descripcion.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Opcional: Explica brevemente la función de este permiso
                  </p>
                </div>
              </form>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 bg-white text-gray-700 px-4 py-3 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isLoading}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmit(onSubmit)}
                  disabled={isLoading}
                  className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Creando...</span>
                    </>
                  ) : (
                    <>
                      <Key className="h-4 w-4" />
                      <span>Crear Permiso</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
      
      {/* Notification Toast */}
      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => {
            setShowNotification(false);
            setNotificationConfig(null);
          }}
          duration={5000}
        />
      )}
    </div>
  );
};